import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, WhatsappLogo, ShieldCheck, Lightning, MapPin, Car, Motorcycle, Truck, Star, CheckCircle, Certificate, Users, Clock, GraduationCap, CaretRight, ClipboardText, BookOpen, Trophy, Coin } from "@phosphor-icons/react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { getDeadlineInfo } from "@/lib/deadline";
import PriceAdvisorModal from "@/components/PriceAdvisorModal";

const ICONS = { Car, Motorcycle, Truck };

export default function Home() {
  const [stats, setStats] = useState({ graduates: 33000, lesson_hours: 145000, vehicles: 8, experience_years: 37, staff: 10 });
  const [courses, setCourses] = useState([]);
  const [posts, setPosts] = useState([]);
  const [gData, setGData] = useState(null);
  const [priceModal, setPriceModal] = useState(false);
  const [priceClass, setPriceClass] = useState(null);
  const deadline = getDeadlineInfo();
  const urgencyStyles = {
    critical: { dot: "bg-brand-error animate-pulse", bg: "bg-brand-error/10 border-brand-error/30", text: "text-brand-error" },
    warning: { dot: "bg-orange-500 animate-pulse", bg: "bg-orange-50 border-orange-200", text: "text-orange-600" },
    attention: { dot: "bg-brand-primary", bg: "bg-white border-stone-200", text: "text-stone-700" },
    normal: { dot: "bg-brand-success", bg: "bg-white border-stone-200", text: "text-stone-700" },
  }[deadline.urgency];

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
    api.get("/blog").then(r => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/google-reviews").then(r => setGData(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO title="Estem Sürücü Kursu | Gebze - Kocaeli'nin En İyi Ehliyet Kursu"
        description="1989'dan beri Gebze'de MEB akreditasyonlu sürücü eğitimi. 33.000+ mezun, %87 ilk denemede sınav başarısı. B, A1, A2, A sınıfı ehliyet kursu. Online başvuru."
        canonical="https://www.estemsurucukursu.com/" />

      {/* HERO — Editorial / Trust */}
      <section className="bg-trust-gradient border-b border-stone-200/60 relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1.5 text-xs shadow-sm ${urgencyStyles.bg}`}>
              <span className={`w-2 h-2 rounded-full ${urgencyStyles.dot}`}></span>
              <span className={`font-semibold ${urgencyStyles.text}`}>{deadline.badge}</span>
            </div>

            <h1 className="mt-6 font-heading text-[44px] md:text-[64px] font-bold tracking-[-0.03em] leading-[1.02] text-brand-ink">
              Gebze'nin köklü <br/>
              <span className="font-serif italic font-normal text-brand-primary">sürücü eğitim</span> kurumu.
            </h1>

            {(deadline.urgency === "critical" || deadline.urgency === "warning") && (
              <div className={`mt-4 p-3 rounded-lg border ${urgencyStyles.bg}`}>
                <div className={`font-bold text-sm ${urgencyStyles.text}`}>⚠ {deadline.headline}</div>
                <div className="text-xs text-stone-700 mt-1">{deadline.subline}</div>
              </div>
            )}


            <p className="mt-6 text-lg text-stone-600 max-w-xl leading-relaxed">
              <strong className="text-brand-ink font-semibold">1989'dan bu yana</strong> Gebze ve Kocaeli'nde 33.000'i aşkın kursiyere ehliyet kazandırdık. MEB akreditasyonlu eğitim, modern araç filosu ve %87 ilk denemede sınav başarısı ile sektörün referans noktasıyız.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/basla" className="nb-btn !px-6 !py-3.5 !text-sm" data-testid="hero-cta-register">
                Online Başvuru <ArrowRight weight="bold" size={16}/>
              </Link>
              <a href="tel:+902626463646" className="nb-btn-outline !px-5 !py-3.5 !text-sm" data-testid="hero-cta-call"><Phone weight="bold" size={16}/> 0262 646 3 646</a>
            </div>

            {/* Trust signals row */}
            <div className="mt-10 pt-8 border-t border-stone-200 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-lg">
              <div className="min-w-0">
                <div className="font-heading font-bold text-xl text-brand-ink leading-tight">37<span className="text-brand-primary">+</span></div>
                <div className="text-xs text-stone-500 mt-0.5">yıllık deneyim</div>
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-base sm:text-xl text-brand-ink leading-tight whitespace-nowrap">33.000<span className="text-brand-primary">+</span></div>
                <div className="text-xs text-stone-500 mt-0.5">mezun kursiyer</div>
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-xl text-brand-ink leading-tight">%87</div>
                <div className="text-xs text-stone-500 mt-0.5">ilk sınav başarısı</div>
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-base sm:text-xl text-brand-ink leading-tight flex items-center gap-1 whitespace-nowrap">
                  {gData?.user_ratings_total ? `${gData.user_ratings_total}` : "568"}
                  <span className="text-brand-primary">+</span>
                </div>
                <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="shrink-0"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Google yorum
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/hero-estem-480.webp 480w, /hero-estem-900.webp 900w"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 42vw"
                />
                <img
                  src="/hero-estem-900.jpg"
                  srcSet="/hero-estem-900.jpg 900w"
                  sizes="(max-width: 640px) 100vw, 42vw"
                  alt="Estem Sürücü Kursu — eğitmen ve öğrenci direksiyon eğitiminde"
                  className="w-full aspect-[4/5] object-cover rounded-2xl shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)]"
                  fetchpriority="high"
                  decoding="async"
                  width="900"
                  height="600"
                />
              </picture>

              {/* Floating cards */}
              <div className="absolute -left-4 top-8 bg-white border border-stone-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-[200px]">
                <Certificate weight="duotone" size={28} className="text-brand-primary"/>
                <div>
                  <div className="text-[11px] font-medium text-stone-500">Akreditasyon</div>
                  <div className="font-bold text-sm text-brand-ink">MEB Onaylı</div>
                </div>
              </div>
              <Link to="/basla" className="group absolute -right-4 bottom-12 bg-white border border-stone-200 rounded-xl shadow-lg pl-4 pr-3 py-3 flex items-center gap-3 hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="hero-floating-cta">
                <div className="w-11 h-11 bg-brand-primary text-white rounded-lg flex items-center justify-center relative shrink-0">
                  <Lightning weight="fill" size={22}/>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand-error rounded-full animate-ping"></span>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand-error rounded-full"></span>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-brand-primary uppercase tracking-wider">Hızlı Başvuru</div>
                  <div className="font-bold text-sm text-brand-ink leading-tight">5 dakikada online başvuru</div>
                  <div className="text-[10px] text-stone-500">TC kimlik kartı yeterli</div>
                </div>
                <ArrowRight weight="bold" size={18} className="text-brand-primary group-hover:translate-x-0.5 transition-transform"/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar - certifications */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {[
            { i: Certificate, t: "MEB Akreditasyonlu", s: "T.C. Milli Eğitim Bakanlığı" },
            { i: ShieldCheck, t: "37 Yıllık Tecrübe", s: "1989'dan beri sektörde" },
            { i: Users, t: "10 Eğitmen Kadrosu", s: "Tamamı sertifikalı" },
            { i: GraduationCap, t: "%87 Başarı Oranı", s: "İlk denemede sınav geçişi" },
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-3">
              <x.i weight="duotone" size={32} className="text-brand-primary shrink-0"/>
              <div>
                <div className="font-semibold text-sm text-brand-ink leading-tight">{x.t}</div>
                <div className="text-xs text-stone-500 mt-0.5">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COURSES — Professional grid */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="courses-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-end mb-12">
            <div>
              <div className="nb-badge mb-4">Kurslarımız</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-brand-ink">
                Her sınıf için <span className="font-serif italic font-normal text-brand-primary">profesyonel</span> eğitim.
              </h2>
            </div>
            <p className="text-stone-600 md:text-right md:max-w-md md:ml-auto">B (manuel/otomatik), A1, A2 ve A motosiklet sınıflarında MEB müfredatına uygun, modern eğitim araçlarıyla eğitim sunuyoruz.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map((c) => {
              const Icon = ICONS[c.icon] || Car;
              const slug = c.code === "B" ? "b-sinifi" : c.code === "B-Auto" ? "b-otomatik" : c.code === "CDE" ? "c-d-e" : c.code.toLowerCase();
              return (
                <Link key={c.code} to={`/kurs/${slug}`} className="nb-card p-7 group flex flex-col" data-testid={`course-card-${c.code}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 bg-brand-primarySubtle text-brand-primary rounded-lg flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors">
                      <Icon size={26} weight="bold"/>
                    </div>
                    {c.popular && <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-primary">Popüler</span>}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-ink">{c.name}</h3>
                  <p className="mt-2 text-sm text-stone-600 flex-1 leading-relaxed">{c.description}</p>
                  <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between">
                    <button
                      onClick={e => { e.preventDefault(); setPriceClass(c.code); setPriceModal(true); }}
                      data-testid={`home-price-btn-${c.code}`}
                      className="flex items-center gap-1.5 text-sm font-bold text-brand-primary bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <Coin size={13} weight="fill"/> Fiyatları Gör
                    </button>
                    <span className="text-sm font-semibold text-brand-primary group-hover:gap-2 inline-flex items-center gap-1 transition-all">Detay <CaretRight weight="bold" size={14}/></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US - Process section */}
      <section className="bg-white border-y border-stone-200 px-6 md:px-12 py-20 md:py-28" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="nb-badge mb-4">Neden Estem?</div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-ink leading-tight">Güven, deneyim ve <span className="font-serif italic font-normal text-brand-primary">modern altyapı.</span></h2>
              <p className="mt-5 text-stone-600 leading-relaxed">Bir sürücü kursunun en önemli kriteri, kursiyerini hayata güvenli sürücü olarak hazırlamasıdır. Estem, 37 yıllık deneyimini modern eğitim altyapısıyla birleştirir.</p>
              <Link to="/hakkimizda" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-primary hover:gap-2 transition-all">Kurumumuzu tanıyın <ArrowRight weight="bold" size={14}/></Link>
            </div>

            <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
              {[
                { i: Certificate, t: "MEB Akreditasyonu", d: "T.C. Milli Eğitim Bakanlığı tarafından akredite ve düzenli denetlenen müfredat." },
                { i: Users, t: "Deneyimli Kadro", d: "Tamamı resmi belgeli, ortalama 12 yıl deneyimli 10 eğitmen kadrosu." },
                { i: Car, t: "Modern Araç Filosu", d: "13 aktif eğitim aracı — yıllık bakımları tam, 0 arıza toleransı." },
                { i: GraduationCap, t: "Yüksek Başarı", d: "Kursiyerlerimizin %87'si direksiyon sınavını ilk denemede geçer." },
              ].map((x, i) => (
                <div key={i} className="bg-brand-bg border border-stone-200/80 rounded-xl p-6">
                  <x.i weight="duotone" size={28} className="text-brand-primary mb-3"/>
                  <h3 className="font-heading font-bold text-base text-brand-ink">{x.t}</h3>
                  <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS / Eğitim Süreci */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="nb-badge mb-4">Eğitim Süreci</div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-brand-ink">6 adımda <span className="font-serif italic font-normal text-brand-primary">ehliyetinize</span> kavuşun.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
            {[
              { n: "01", icon: ClipboardText, t: "Online Kayıt Olun", d: "Kayıt formunu doldurun, evraklarınızı yükleyin ve süreci başlatın." },
              { n: "02", icon: Phone, t: "Kayıt Onayınızı Alın", d: "Ekibimiz sizinle iletişime geçerek kaydınızı tamamlasın." },
              { n: "03", icon: BookOpen, t: "Teorik Eğitiminizi Tamamlayın", d: "MEB onaylı teorik eğitimlere katılın." },
              { n: "04", icon: CheckCircle, t: "E-Sınavı Başarıyla Geçin", d: "Teorik bilginizi sınavla tamamlayın." },
              { n: "05", icon: Car, t: "Direksiyon Eğitimi Alın", d: "Uzman eğitmenlerle birebir sürüş deneyimi kazanın." },
              { n: "06", icon: Trophy, t: "Ehliyetinizi Alın", d: "Direksiyon sınavını başarıyla tamamlayarak ehliyetinizi alın." },
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 hover:bg-brand-primarySubtle/40 transition-colors group">
                <div className="flex items-start justify-between mb-5">
                  <div className="font-heading font-bold text-3xl text-brand-primary/30 tracking-tight">{s.n}</div>
                  <div className="w-10 h-10 rounded-lg bg-brand-primarySubtle text-brand-primary flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors">
                    <s.icon weight="bold" size={20}/>
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-brand-ink leading-tight">{s.t}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — Professional */}
      <section className="bg-brand-ink text-white px-6 md:px-12 py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-primary mb-4">Referanslar</div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">Kursiyerlerimiz ne <span className="font-serif italic font-normal text-brand-primary">söylüyor?</span></h2>
            <div className="mt-6 flex items-center justify-center gap-1.5 flex-wrap">
              {[...Array(5)].map((_,i) => <Star key={i} weight="fill" className="text-amber-400" size={22}/>)}
              <span className="ml-1 text-stone-200 font-bold text-lg">{gData?.rating?.toFixed(1) ?? "4.8"}</span>
              <span className="text-stone-400 text-sm">/ 5</span>
              <span className="text-stone-400 text-sm mx-1">—</span>
              <span className="text-stone-300 text-sm font-medium">
                {gData?.user_ratings_total ? `${gData.user_ratings_total.toLocaleString("tr-TR")} Google değerlendirmesi` : "247 Google değerlendirmesi"}
              </span>
              {/* Google logosu */}
              <span className="flex items-center gap-1 bg-white/10 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ml-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Google
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {(gData?.reviews?.length
              ? gData.reviews.slice(0, 3)
              : [
                  { name: "Atakan Demir", text: "Sürücü eğitimimi alırken gerek hocalarımız gerekse ofis çalışanları çok ilgililer. Eğitim konusunda gayet iyiler, kayıt olmak isteyenlere tavsiyemdir.", rating: 5, relative_time: "2025" },
                  { name: "Furkan Çelik", text: "Tavsiye ederim, personel ilgili ve kaliteli hizmet sunuyorlar. Sami hocaya özellikle teşekkür ediyorum.", rating: 5, relative_time: "2025" },
                  { name: "Mustafa Aydın", text: "Güzel karşılıyorlar. Süreç boyunca yalnız bırakmıyorlar, her sorunuza cevap veriyorlar.", rating: 5, relative_time: "2024" },
                ]
            ).map((r, i) => (
              <div key={i} className="border border-white/10 rounded-xl p-7 hover:bg-white/[0.03] transition-colors flex flex-col">
                <div className="flex items-center gap-0.5 mb-1">
                  {[...Array(r.rating ?? 5)].map((_,k) => <Star key={k} weight="fill" className="text-amber-400" size={14}/>)}
                </div>
                <p className="mt-3 text-stone-200 leading-relaxed flex-1">"{r.text}"</p>
                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">{r.name}</div>
                    <div className="text-xs text-stone-400 mt-0.5">{r.relative_time}</div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-50"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                </div>
              </div>
            ))}
          </div>

          {gData && (
            <div className="mt-8 text-center">
              <a
                href={gData.maps_uri || "https://www.google.com/maps/place/?q=place_id:ChIJF0qrUokgyxQRMHhh-N0Kizg&hl=tr"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-white transition-colors border border-white/10 hover:border-white/30 rounded-full px-5 py-2"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Google Maps'te tüm {gData.user_ratings_total?.toLocaleString("tr-TR")} yorumu gör
                <ArrowRight weight="bold" size={12}/>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* BLOG PREVIEW */}
      {posts.length > 0 && (
        <section className="px-6 md:px-12 py-20 md:py-28 bg-white border-t border-stone-200" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="nb-badge mb-4">Blog</div>
                <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-brand-ink">Rehberlerimizle <span className="font-serif italic font-normal text-brand-primary">bir adım önde.</span></h2>
              </div>
              <Link to="/blog" className="nb-btn-outline" data-testid="blog-preview-all">Tümünü Gör <ArrowRight weight="bold" size={16}/></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="nb-card overflow-hidden group hover:-translate-y-1 transition-transform" data-testid={`home-blog-${p.slug}`}>
                  {p.cover_image && (
                    <img src={p.cover_image} alt={p.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"/>
                  )}
                  <div className="p-6">
                    <div className="text-xs font-semibold text-stone-500">{new Date(p.created_at).toLocaleDateString("tr-TR")}</div>
                    <h3 className="mt-2 font-heading font-bold text-lg leading-tight line-clamp-2">{p.title}</h3>
                    <p className="mt-2 text-sm text-stone-600 line-clamp-2">{p.excerpt}</p>
                    <div className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-brand-primary">Devamını Oku <ArrowRight weight="bold" size={14}/></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* SERVICE AREA */}
      <section className="px-6 md:px-12 py-20 bg-white border-y border-stone-200" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="nb-badge mb-4">Hizmet Bölgemiz</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-ink">Gebze, Darıca ve Çayırova'nın <span className="font-serif italic font-normal text-brand-primary">her mahallesinden</span> tercih ediliyoruz.</h2>
            <p className="mt-4 text-stone-600 max-w-2xl mx-auto">Hacı Halil Mahallesi'ndeki merkez ofisimiz, Gebze, Darıca ve Çayırova'nın tüm mahallelerinden kolayca ulaşılabilir konumdadır.</p>
          </div>
          <div className="max-w-5xl mx-auto space-y-6">
            {[
              {
                label: "Gebze",
                items: ["Hacı Halil","Mustafapaşa","Osmanyılmaz","Sultan Orhan","Mevlana","Cumhuriyet","Pelitli","Tatlıkuyu","Tavşancıl","Şekerpınar","Güzeller","Beylikbağı","Kirazpınar","Güzelkent","Osmangazi","Gebze Merkez"],
              },
              {
                label: "Darıca",
                items: ["Darıca Merkez","Bağlarbaşı","Fatih","Yeni Mahalle","Bayraklı","Emek","Nene Hatun"],
              },
              {
                label: "Çayırova",
                items: ["Çayırova Merkez","Atatürk","Akse","Balkaya","Eskigebze","İstasyon","Yavuz Selim"],
              },
            ].map(group => (
              <div key={group.label}>
                <div className="text-[11px] font-bold tracking-widest text-stone-400 uppercase mb-2.5">{group.label}</div>
                <div className="flex flex-wrap gap-2">
                  {group.items.map(n => (
                    <div key={n} className="px-3 py-1.5 border border-stone-200 rounded-lg bg-brand-bg text-xs font-medium text-stone-700 hover:border-brand-primary hover:text-brand-primary transition-colors cursor-default">{n}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="final-cta">
        <div className="max-w-7xl mx-auto bg-brand-ink rounded-3xl overflow-hidden relative">
          <div className="absolute inset-0 bg-dots opacity-30"></div>
          <div className="relative grid md:grid-cols-2 gap-8 p-10 md:p-16 items-center">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-primary">Başvuru Süreci</div>
              <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight">Bugün başlayın, <br/><span className="font-serif italic font-normal text-brand-primary">yarın direksiyona</span> geçin.</h2>
              <p className="mt-5 text-stone-300 max-w-md leading-relaxed">Online başvurunuzu tamamlayın, belgelerinizi yükleyin. Ekibimiz 48 saat içinde sizinle iletişime geçer.</p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
              <Link to="/basla" className="nb-btn !bg-white !text-brand-ink hover:!bg-stone-100 !px-6 !py-4 !text-base" data-testid="final-cta-btn">Online Başvuru Başlat <ArrowRight weight="bold" size={18}/></Link>
              <a href="tel:+902626463646" className="nb-btn-outline !border-white/20 !text-white !bg-transparent hover:!bg-white/5 !px-6 !py-4"><Phone weight="bold" size={16}/> 0262 646 3 646</a>
            </div>
          </div>
        </div>
      </section>

      <PriceAdvisorModal
        open={priceModal}
        onClose={() => setPriceModal(false)}
        defaultClass={priceClass}
      />
    </>
  );
}
